import React, { useEffect, useMemo, useState } from "react";
import { fmtDate, fmtDateTime } from "../../lib/format";
import {
  DELIVERY_STATUS_LABEL,
  FILTERS,
  FILTER_LABEL,
  INVITATION_STATUS_LABEL,
  cooldownText,
  deliveryErrorMessage,
  roleLabel,
} from "../../lib/memberManagement";

const STATUS_TONE = {
  pending: "sent",
  accepted: "accepted",
  revoked: "declined",
  expired: "expired",
};

/**
 * One countdown, driven by the number the DATABASE reported.
 *
 * The tick is cosmetic: reaching zero re-enables the button, but the server
 * refuses anything inside its own window regardless, so a wrong clock in the
 * browser can only ever be too cautious.
 */
function useCooldown(seconds, invitationId) {
  const [left, setLeft] = useState(() => Math.max(Number(seconds) || 0, 0));
  useEffect(() => {
    setLeft(Math.max(Number(seconds) || 0, 0));
  }, [seconds, invitationId]);
  useEffect(() => {
    if (left <= 0) return undefined;
    const timer = setInterval(() => setLeft((v) => (v <= 1 ? 0 : v - 1)), 1000);
    return () => clearInterval(timer);
  }, [left]);
  return left;
}

const InvitationRow = ({ invitation, busyId, onRevoke, onResend, onRecreate }) => {
  const left = useCooldown(invitation.resend_available_in_seconds, invitation.id);
  const busy = busyId === invitation.id;
  const status = invitation.status || "pending";
  const deliveryError =
    invitation.delivery_status === "delivery_failed"
      ? deliveryErrorMessage(invitation.last_delivery_error_code)
      : "";
  const name =
    [invitation.first_name, invitation.last_name].filter(Boolean).join(" ") || "—";
  const canResendNow = invitation.can_resend && left <= 0;

  return (
    <tr data-testid={`invitation-row-${invitation.id}`}>
      <td>
        {name}
        <div className="qw-help" style={{ overflowWrap: "anywhere" }}>
          {invitation.invited_email}
        </div>
        {invitation.phone && <div className="qw-help">{invitation.phone}</div>}
      </td>
      <td data-testid={`invitation-role-${invitation.id}`}>
        {invitation.role_label || roleLabel(invitation.target_role)}
      </td>
      <td>
        <span
          className={`qw-badge ${STATUS_TONE[status] || "expired"}`}
          data-testid={`invitation-status-${invitation.id}`}
        >
          <span className="dot" aria-hidden="true" />
          {INVITATION_STATUS_LABEL[status] || status}
        </span>
        {invitation.reserves_seat && (
          <div className="qw-help" data-testid={`invitation-reserves-seat-${invitation.id}`}>
            Заема лицензирано място
          </div>
        )}
      </td>
      <td>
        <span data-testid={`invitation-delivery-${invitation.id}`}>
          {DELIVERY_STATUS_LABEL[invitation.delivery_status] || "—"}
        </span>
        {deliveryError && (
          <div className="qw-error" data-testid={`invitation-delivery-error-${invitation.id}`}>
            {deliveryError}
          </div>
        )}
        {Number(invitation.resend_count) > 0 && (
          <div className="qw-help" data-testid={`invitation-resend-count-${invitation.id}`}>
            Изпратена отново {Number(invitation.resend_count)} пъти
          </div>
        )}
      </td>
      <td>{fmtDate(invitation.created_at)}</td>
      <td data-testid={`invitation-expires-${invitation.id}`}>
        {fmtDateTime(invitation.expires_at)}
      </td>
      <td>
        <div className="qw-row-actions">
          {invitation.can_resend && (
            <button
              type="button"
              className="qw-btn sm"
              onClick={() => onResend(invitation)}
              disabled={busy || !canResendNow}
              aria-describedby={
                !canResendNow ? `invitation-cooldown-${invitation.id}` : undefined
              }
              data-testid={`invitation-resend-${invitation.id}`}
            >
              {busy ? "Изпращане…" : "Изпрати отново"}
            </button>
          )}
          {invitation.can_revoke && (
            <button
              type="button"
              className="qw-btn danger sm"
              onClick={() => onRevoke(invitation)}
              disabled={busy}
              data-testid={`invitation-revoke-${invitation.id}`}
            >
              Отмени поканата
            </button>
          )}
          {invitation.can_recreate && (
            <button
              type="button"
              className="qw-btn sm"
              onClick={() => onRecreate(invitation)}
              data-testid={`invitation-recreate-${invitation.id}`}
            >
              Създай нова покана
            </button>
          )}
          {!invitation.can_resend && !invitation.can_revoke && !invitation.can_recreate && (
            <span className="qw-help">—</span>
          )}
        </div>
        {invitation.can_resend && !canResendNow && (
          <div
            className="qw-help"
            id={`invitation-cooldown-${invitation.id}`}
            data-testid={`invitation-cooldown-${invitation.id}`}
          >
            {cooldownText(left)}
          </div>
        )}
      </td>
    </tr>
  );
};

/**
 * The invitation roster: who was invited, what happened to the e-mail, whether
 * the invitation still holds a licensed seat, and what the ACTING person may do
 * with it.
 *
 * Every permission flag (can_revoke, can_resend, can_recreate) and the remaining
 * cooldown are computed by the database. This component renders them; it never
 * derives them from a role held in the browser.
 *
 * A delivery failure is never hidden and never described as a lost invitation:
 * the row stays, the seat stays reserved and a safe Bulgarian sentence explains
 * the sanitized code.
 */
export const InvitationRoster = ({
  items,
  loading,
  error,
  filter,
  onFilter,
  busyId,
  onRevoke,
  onResend,
  onRecreate,
}) => {
  const rows = useMemo(() => items || [], [items]);

  return (
    <div>
      <div
        className="qw-seg"
        role="group"
        aria-label="Филтър по статус на поканата"
        data-testid="invitation-filter"
        style={{ marginBottom: 12 }}
      >
        {FILTERS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onFilter(value)}
            aria-pressed={filter === value}
            data-testid={`invitation-filter-${value}`}
          >
            {FILTER_LABEL[value]}
          </button>
        ))}
      </div>

      {loading && (
        <div className="qw-card qw-empty" data-testid="invitation-roster-loading">
          Зареждане на поканите…
        </div>
      )}

      {!loading && error && (
        <div
          className="qw-card qw-empty"
          style={{ borderColor: "#fecaca", color: "#7f1d1d", background: "#fef2f2" }}
          data-testid="invitation-roster-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="qw-card qw-empty" data-testid="invitation-roster-empty">
          Няма изпратени покани.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="qw-table-wrap" tabIndex={0} role="region" aria-label="Покани">
          <table className="qw-table" data-testid="invitation-roster-table">
            <thead>
              <tr>
                <th scope="col">Поканен</th>
                <th scope="col">Роля</th>
                <th scope="col">Статус</th>
                <th scope="col">Изпращане</th>
                <th scope="col">Създадена</th>
                <th scope="col">Валидна до</th>
                <th scope="col">Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invitation) => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  busyId={busyId}
                  onRevoke={onRevoke}
                  onResend={onResend}
                  onRecreate={onRecreate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitationRoster;
